import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Users, Sparkles, Globe, ChevronDown, MessageCircle, Award, MapPin, Swords, Menu, X, BookOpen } from 'lucide-react';
import CustomCursor from './CustomCursor';
import ServerStatus from './ServerStatus';

const PokeMovieSite = () => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    const features = [
        {
            icon: <Sparkles className="w-10 h-10" />,
            title: "Plus de 900 Pokémon",
            description: "Tous les Pokémon des générations 1 à 9 disponibles avec leurs formes régionales et shinies",
            image: "https://images.unsplash.com/photo-1613771404721-1f92d799e49f?w=600&h=400&fit=crop"
        },
        {
            icon: <Swords className="w-10 h-10" />,
            title: "Combats Stratégiques",
            description: "Système de combat fidèle aux jeux Pokémon avec EV/IV, talents cachés et méta compétitive",
            image: "/images/fly.jpeg"
        },
        {
            icon: <MapPin className="w-10 h-10" />,
            title: "Monde Immense",
            description: "Explore des biomes uniques, des donjons secrets et des arènes construites sur-mesure",
            image: "/images/sunset.jpg"
        },
        {
            icon: <Award className="w-10 h-10" />,
            title: "Events & Tournois",
            description: "Participe à des événements hebdomadaires, chasses shinies communautaires et tournois",
            image: "/images/fight.jpg"
        }
    ];

    const showcaseItems = [
        {
            title: "Captures Épiques",
            slug: "captures-epiques",
            description: "Parcours le monde à la recherche de Pokémon légendaires dans leurs habitats naturels",
            image: "/images/charizard.png"
        },
        {
            title: "Dresse ton Équipe",
            slug: "dresse-ton-equipe",
            description: "Entraîne tes Pokémon, développe leur potentiel et crée l'équipe parfaite",
            image: "/images/academy.png"
        },
        {
            title: "Affronte des Dresseurs",
            slug: "mega-evolutions-guide-complet",
            description: "Défie d'autres joueurs en PvP, participe aux ligues et deviens champion",
            image: "/images/fight.jpg"
        }
    ];

    return (
        <div ref={containerRef} className="relative min-h-screen bg-white overflow-hidden">
            <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');

                * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
            }

                body {
                font-family: 'Nunito', sans-serif;
                overflow-x: hidden;
            }

                .pokemon-red {
                color: #DC0A2D;
            }

                .pokemon-blue {
                color: #3B4CCA;
            }

                .pokemon-yellow {
                color: #FFCB05;
            }

                .bg-pokemon-red {
                background-color: #DC0A2D;
            }

                .bg-pokemon-blue {
                background-color: #3B4CCA;
            }

                .bg-pokemon-yellow {
                background-color: #FFCB05;
            }

                .pokeball-pattern {
                position: absolute;
                width: 100%;
                height: 100%;
                background-image:
                radial-gradient(circle at 20% 30%, rgba(220, 10, 45, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 80% 70%, rgba(59, 76, 202, 0.03) 0%, transparent 50%);
            }

                .floating-pokeball {
                animation: float 8s ease-in-out infinite;
            }

                @keyframes float {
                0%, 100% { transform: translateY(0) rotate(0deg); }
                25% { transform: translateY(-30px) rotate(5deg); }
                75% { transform: translateY(-15px) rotate(-5deg); }
            }

                .text-pokemon-gradient {
                background: linear-gradient(135deg, #DC0A2D 0%, #FFCB05 50%, #3B4CCA 100%);
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
            }

                .hero-shadow {
                box-shadow:
                0 20px 60px rgba(220, 10, 45, 0.3),
                0 30px 90px rgba(59, 76, 202, 0.2);
            }

                .card-hover {
                transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            }

                .card-hover:hover {
                transform: translateY(-12px);
                box-shadow: 0 30px 80px rgba(220, 10, 45, 0.25);
            }

                .shine-effect {
                position: relative;
                overflow: hidden;
            }

                .shine-effect::before {
                content: '';
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
                transition: left 0.5s;
            }

                .shine-effect:hover::before {
                left: 100%;
            }

                .pokeball-divider {
                position: relative;
                height: 60px;
                background: linear-gradient(to right, transparent, #DC0A2D 20%, #DC0A2D 80%, transparent);
            }

            .pokeball-center {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              z-index: 10;
            }
            
            .parallax-img {
              transition: transform 0.3s ease;
            }
            
            .stat-number {
              font-variant-numeric: tabular-nums;
            }
            
            @keyframes pulse-glow {
              0%, 100% {
                box-shadow: 0 0 20px rgba(220, 10, 45, 0.5);
              }
              50% {
                box-shadow: 0 0 40px rgba(220, 10, 45, 0.8);
              }
            }
            
            .pulse-glow {
              animation: pulse-glow 2s ease-in-out infinite;
            }
      `}</style>

            {/* Custom Pokeball Cursor */}
            <CustomCursor />

            {/* Background Pattern */}
            <div className="pokeball-pattern" />

            {/* Navigation */}
            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <motion.a
                        href="/"
                        className="flex items-center gap-4"
                        whileHover={{ scale: 1.05 }}
                    >
                        <div className="relative w-14 h-14">
                            {/* Pokeball Logo */}
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full border-2 border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '-0.02em' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </motion.a>

                    {/* Desktop Menu */}
                    <div className="hidden lg:flex items-center gap-8">
                        {[
                            { name: 'Accueil', href: '/' },
                            { name: 'Fonctionnalités', href: '/fonctionnalites' },
                            { name: 'Communauté', href: '/communaute' },
                            { name: 'Wiki', href: '/wiki' }
                        ].map((item, i) => (
                            <motion.a
                                key={item.name}
                                href={item.href}
                                className="text-gray-700 hover:text-red-600 font-bold text-lg transition-colors relative group"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                                initial={{ opacity: 0, y: -20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 + i * 0.1 }}
                            >
                                {item.name}
                                <span className="absolute -bottom-1 left-0 w-0 h-1 bg-red-600 group-hover:w-full transition-all duration-300" />
                            </motion.a>
                        ))}
                    </div>

                    <div className="flex items-center gap-4">
                        <motion.button
                            className="hidden sm:flex items-center gap-3 px-8 py-3.5 rounded-full bg-gradient-to-r from-red-600 to-red-700 text-white font-black text-lg shadow-2xl shine-effect hover:shadow-red-600/50 transition-all"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Play className="w-5 h-5" fill="white" />
                            JOUER
                        </motion.button>

                        <button
                            className="lg:hidden p-2 text-gray-700"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile Menu */}
                <AnimatePresence>
                    {mobileMenuOpen && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="lg:hidden border-t-2 border-gray-200 bg-white"
                        >
                            <div className="px-6 py-4 space-y-3">
                                {[
                                    { name: 'Accueil', href: '/' },
                                    { name: 'Fonctionnalités', href: '/fonctionnalites' },
                                    { name: 'Communauté', href: '/communaute' },
                                    { name: 'Wiki', href: '/wiki' }
                                ].map(item => (
                                    <a
                                        key={item.name}
                                        href={item.href}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className="block text-gray-700 hover:text-red-600 font-bold text-lg"
                                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                                    >
                                        {item.name}
                                    </a>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.nav>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center px-6 pt-32 pb-20 bg-gradient-to-br from-red-50 via-white to-blue-50">
                <div className="max-w-7xl mx-auto w-full">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        {/* Left Content */}
                        <motion.div
                            initial={{ opacity: 0, x: -100 }}
                            animate={{ opacity: isLoaded ? 1 : 0, x: isLoaded ? 0 : -100 }}
                            transition={{ duration: 0.8 }}
                            className="space-y-8"
                        >
                            <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
                                className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-red-600 text-white font-bold shadow-xl"
                            >
                                <Sparkles className="w-5 h-5" />
                                <span style={{ fontFamily: 'Montserrat, sans-serif' }}>Serveur #1 Français</span>
                            </motion.div>

                            <h1 className="text-6xl lg:text-7xl xl:text-8xl font-black leading-[0.95]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                <span className="text-gray-900">Deviens un</span>
                                <br />
                                <span className="text-pokemon-gradient">Maître Pokémon</span>
                            </h1>

                            <p className="text-2xl text-gray-700 leading-relaxed font-semibold" style={{ fontFamily: 'Nunito, sans-serif' }}>
                                L'expérience <span className="text-red-600 font-black">Cobblemon</span> ultime !
                                Capture, dresse et combats dans un monde Minecraft fidèle à l'univers Pokémon.
                            </p>

                            <div className="flex flex-wrap gap-4">
                                <motion.button
                                    className="group px-10 py-5 rounded-2xl bg-red-600 text-white font-black text-xl flex items-center gap-4 hero-shadow shine-effect hover:bg-red-700 transition-all"
                                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                                    whileHover={{ scale: 1.05, y: -3 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <Play className="w-7 h-7 group-hover:scale-110 transition-transform" fill="white" />
                                    COMMENCER L'AVENTURE
                                </motion.button>

                                <motion.button
                                    onClick={() => window.open('https://discord.gg/3wS59h3Vbu', '_blank')}
                                    className="px-10 py-5 rounded-2xl bg-white border-4 border-red-600 text-red-600 font-black text-xl hover:bg-red-50 transition-all shadow-xl"
                                    style={{ fontFamily: 'Montserrat, sans-serif' }}
                                    whileHover={{ scale: 1.05, y: -3 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    REJOINDRE DISCORD
                                </motion.button>
                            </div>

                            {/* Server Status */}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.8 }}
                                className="flex justify-center pt-6"
                            >
                                <ServerStatus />
                            </motion.div>

                            {/* Stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8">
                                {[
                                    { label: "Joueurs", value: "250+", icon: <Users className="w-6 h-6" /> },
                                    { label: "Pokémon", value: "900+", icon: <Sparkles className="w-6 h-6" /> },
                                    { label: "Arènes", value: "24", icon: <Award className="w-6 h-6" /> },
                                    { label: "Biomes", value: "40+", icon: <MapPin className="w-6 h-6" /> }
                                ].map((stat, i) => (
                                    <motion.div
                                        key={stat.label}
                                        initial={{ opacity: 0, scale: 0 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: 0.6 + i * 0.1, type: "spring" }}
                                        className="text-center p-4 rounded-2xl bg-white border-2 border-gray-200 hover:border-red-600 transition-all shadow-lg"
                                    >
                                        <div className="flex justify-center mb-2 text-red-600">
                                            {stat.icon}
                                        </div>
                                        <div className="text-4xl font-black text-gray-900 stat-number mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                            {stat.value}
                                        </div>
                                        <div className="text-sm font-bold text-gray-600 uppercase tracking-wide">{stat.label}</div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Right Content - Hero Image */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
                            animate={{ opacity: isLoaded ? 1 : 0, scale: isLoaded ? 1 : 0.8, rotate: 0 }}
                            transition={{ duration: 1, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative floating-pokeball">
                                {/* Main showcase image - Replace with real Cobblemon screenshot */}
                                <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white">
                                    <img
                                        src="/images/champion.png"
                                        alt="PokeMovie Player"
                                        className="w-full h-auto"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                                </div>

                                {/* Floating stat cards */}
                                <motion.div
                                    animate={{ y: [0, -10, 0] }}
                                    transition={{ duration: 3, repeat: Infinity }}
                                    className="absolute -top-6 -left-6 bg-white rounded-2xl shadow-2xl p-4 border-4 border-yellow-400"
                                >
                                    <div className="text-3xl mb-1">⚡</div>
                                    <div className="text-sm font-bold text-gray-600">Pokémon</div>
                                    <div className="text-2xl font-black text-yellow-500">900+</div>
                                </motion.div>

                                <motion.div
                                    animate={{ y: [0, 10, 0] }}
                                    transition={{ duration: 4, repeat: Infinity }}
                                    className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-2xl p-4 border-4 border-red-600"
                                >
                                    <div className="text-3xl mb-1">🏆</div>
                                    <div className="text-sm font-bold text-gray-600">Dresseur</div>
                                    <div className="text-xl font-black text-red-600">Top #1</div>
                                </motion.div>
                            </div>
                        </motion.div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    className="absolute bottom-10 left-0 right-0 flex justify-center"
                    animate={{ y: [0, 15, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                >
                    <ChevronDown className="w-10 h-10 text-red-600" strokeWidth={3} />
                </motion.div>
            </section>

            {/* Pokeball Divider */}
            <div className="pokeball-divider">
                <div className="pokeball-center">
                    <div className="relative w-16 h-16">
                        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                        <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                        <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                        <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-900 transform -translate-y-1/2 z-10"></div>
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 bg-white rounded-full border-2 border-gray-900 z-20"></div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <section className="relative py-32 px-6 bg-gradient-to-br from-blue-50 via-white to-red-50">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-20"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            whileInView={{ scale: 1 }}
                            viewport={{ once: true }}
                            className="inline-block mb-6"
                        >
                            <div className="text-6xl">⚡</div>
                        </motion.div>
                        <h2 className="text-5xl lg:text-6xl font-black text-gray-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Une Expérience <span className="text-red-600">Unique</span>
                        </h2>
                        <p className="text-2xl text-gray-700 max-w-3xl mx-auto font-semibold">
                            PokeMovie combine le meilleur de Minecraft et Pokémon pour créer l'aventure ultime
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 gap-8">
                        {features.map((feature, i) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: i * 0.15, duration: 0.6 }}
                                className="card-hover group"
                            >
                                <div className="relative h-full p-8 rounded-3xl bg-white border-4 border-gray-200 group-hover:border-red-600 shadow-xl overflow-hidden">
                                    {/* Feature Image */}
                                    <div className="relative mb-6 rounded-2xl overflow-hidden h-56">
                                        <img
                                            src={feature.image}
                                            alt={feature.title}
                                            className="w-full h-full object-cover parallax-img group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                        <div className="absolute bottom-4 left-4 w-14 h-14 rounded-2xl bg-red-600 flex items-center justify-center text-white shadow-2xl">
                                            {feature.icon}
                                        </div>
                                    </div>

                                    <h3 className="text-3xl font-black text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {feature.title}
                                    </h3>
                                    <p className="text-lg text-gray-700 leading-relaxed font-semibold">
                                        {feature.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Showcase Section */}
            <section className="relative py-32 px-6 bg-gray-900">
                <div className="max-w-7xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-20"
                    >
                    </motion.div>

                    <div className="space-y-24">
                        {showcaseItems.map((item, i) => (
                            <motion.div
                                key={item.title}
                                initial={{ opacity: 0, x: i % 2 === 0 ? -100 : 100 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.8 }}
                                className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
                            >
                                <div className={i % 2 === 1 ? 'lg:col-start-2' : ''}>
                                    <h3 className="text-4xl lg:text-5xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                        {item.title}
                                    </h3>
                                    <p className="text-xl text-gray-300 leading-relaxed font-semibold mb-8">
                                        {item.description}
                                    </p>
                                    <motion.button
                                        onClick={() => window.location.href = `/blog/${item.slug}`}
                                        className="px-8 py-4 rounded-xl bg-red-600 text-white font-black text-lg shine-effect hover:bg-red-700 transition-all shadow-2xl"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        En Savoir Plus
                                    </motion.button>
                                </div>
                                <div className={i % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''}>
                                    <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white/10">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* NOUVEAU : Bouton Blog */}
                    <div className="mt-24 text-center">
                        <motion.button
                            onClick={() => window.location.href = '/blog'}
                            className="inline-flex items-center gap-3 px-12 py-6 rounded-2xl bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 font-black text-2xl shine-effect hover:from-yellow-500 hover:to-orange-600 transition-all shadow-2xl"
                            style={{ fontFamily: 'Montserrat, sans-serif' }}
                            whileHover={{ scale: 1.05, y: -5 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <BookOpen className="w-8 h-8" />
                            Voir Tous les Articles
                        </motion.button>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="relative py-32 px-6 bg-gradient-to-br from-red-600 via-red-700 to-red-800">
                <div className="max-w-5xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="space-y-8"
                    >
                        <div className="text-7xl mb-6">🎮</div>
                        <h2 className="text-5xl lg:text-7xl font-black text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Rejoins l'Aventure !
                        </h2>
                        <p className="text-2xl text-red-100 max-w-3xl mx-auto font-semibold mb-12">
                            Plus de 250 dresseurs t'attendent. Lance Minecraft et connecte-toi maintenant !
                        </p>

                        <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                            <div className="flex items-center gap-4 px-8 py-5 rounded-2xl bg-white/10 backdrop-blur-sm border-4 border-white/30">
                                <Globe className="w-8 h-8 text-yellow-400" />
                                <code className="text-white font-mono font-black text-2xl">play.pokemovie.fr</code>
                            </div>
                            <motion.button
                                className="px-10 py-5 rounded-2xl bg-white text-red-600 font-black text-xl shine-effect hover:bg-yellow-400 hover:text-red-700 transition-all shadow-2xl"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                                whileHover={{ scale: 1.05, y: -3 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                COPIER L'IP
                            </motion.button>
                        </div>

                        <div className="flex gap-6 justify-center pt-12">
                            {[
                                { icon: <MessageCircle />, label: 'Discord', link: 'https://discord.gg/3wS59h3Vbu' },
                                { icon: <Users />, label: 'Forum', link: '#' },
                                { icon: <Award />, label: 'Wiki', link: '/wiki' }
                            ].map((social, i) => (
                                <motion.a
                                    key={social.label}
                                    href={social.link}
                                    target="_blank"
                                    className="group flex flex-col items-center gap-2"
                                    whileHover={{ y: -5 }}
                                >
                                    <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-sm border-4 border-white/30 flex items-center justify-center text-white group-hover:bg-white group-hover:text-red-600 transition-all">
                                        {social.icon}
                                    </div>
                                    <span className="text-white/80 font-bold text-sm">{social.label}</span>
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="relative py-16 px-6 bg-gray-900 border-t-4 border-red-600">
                <div className="max-w-7xl mx-auto">
                    <div className="grid md:grid-cols-3 gap-12 mb-12">
                        <div>
                            <div className="flex items-center gap-3 mb-4">
                                {/* Pokeball Logo */}
                                <div className="relative w-12 h-12">
                                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                                    <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                                    <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                                    <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-900 transform -translate-y-1/2"></div>
                                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 border-gray-900"></div>
                                </div>
                                <div className="text-2xl font-black text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                    PokeMovie
                                </div>
                            </div>
                            <p className="text-gray-400 leading-relaxed">
                                Le serveur Cobblemon français le plus immersif. Rejoins une communauté passionnée !
                            </p>
                        </div>

                        <div>
                            <h4 className="text-white font-black text-lg mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                Liens Rapides
                            </h4>
                            <ul className="space-y-2">
                                {['Règlement', 'Wiki', 'Support', 'Vote'].map(link => (
                                    <li key={link}>
                                        <a href="#" className="text-gray-400 hover:text-white transition-colors font-semibold">
                                            {link}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-white font-black text-lg mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                Communauté
                            </h4>
                            <ul className="space-y-2">
                                {['Discord', 'Twitter', 'YouTube', 'TikTok'].map(link => (
                                    <li key={link}>
                                        <a href="#" className="text-gray-400 hover:text-white transition-colors font-semibold">
                                            {link}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-gray-800 text-center">
                        <p className="text-gray-500 font-semibold">
                            © 2025 PokeMovie. Serveur Cobblemon non-officiel. Pokémon © Nintendo/Game Freak.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default PokeMovieSite;